import PropTypes from "prop-types";
import get from "lodash.get";
import isEmpty from "lodash.isempty";
import Typography from "../../atoms/typography";
import RateAndReview from "../rateAndReview";
import InformationButton from "../../molecules/informationButton";
import { onSmoothScrollToElement, updateCurrency } from "../../../utils/common";
import { shallowEqual, useSelector } from "react-redux";
import { constants, CTapCampaignConstants } from "../../../constants/pageConstants/pdpConstants";
import { DETAILS, INCLUSIVE_OF_ALL_TAXES, MAXIMUM_RETAIL_PRICE, PRICE } from "../../../constants/common/common";
import Brick from "../../atoms/brick";
import { useEffect, useState } from "react";

/**
 * ProductPriceSection Component
 *
 * Renders the product details section including the product name, price, discount, and review information.
 *
 * @component
 * @param {object} props - Component properties.
 * @param {string} props.containerPadding - Custom padding classes for the container.
 * @param {object} props.currentItem - The current product data object.
 * @param {string} props.selectedCurrency - The selected currency code.
 * @param {boolean} props.isMobile - Flag to determine if the view is mobile.
 * @returns {JSX.Element}
 */
const ProductPriceSection = ({
  containerPadding,
  currentItem,
  selectedCurrency,
  isMobile,
  serverData,
  isOutOfStock,
  isInternational,
}) => {
  const { updatedDeliveryDatesAndGeoPrice, surgePriceDetails } = useSelector(
    (state) => ({
      updatedDeliveryDatesAndGeoPrice: state?.shippingDateAndSlotSelection?.updatedDeliveryDatesAndGeoPrice,
      surgePriceDetails: state.pdp.surgePriceDetails,
    }),
    shallowEqual,
  );

  const [freeGiftTag, setFreeGiftTag] = useState(null);
  const deliveryDatesAndGeoPrice = isInternational ? updatedDeliveryDatesAndGeoPrice : {};

  /**
   *
   * Handles the "CT_web_native_display" event and sets the appropriate state
   * based on the delivery topic.
   *
   * @param {object} event .
   */
  const handleEvent = (event) => {
    if (event.detail.kv.topic === CTapCampaignConstants?.PDP_FREE_GIFT_TOPIC_NAME) {
      setFreeGiftTag(event.detail.kv);
    }
  };

  useEffect(() => {
    document.addEventListener("CT_web_native_display", handleEvent);

    return () => {
      document.removeEventListener("CT_web_native_display", handleEvent);
    };
  }, []);

  /**
   * render surge notification
   */
  const renderSurgeNotificationMessage = () => {
    return (
      <div className="p-16 md:p-0">
        <div className="text-sm text-gray-500">{surgePriceDetails?.notificationMessage}</div>
      </div>
    );
  };
  /**
   * Renders a div containing price information with details.
   * @returns {JSX.Element} The JSX element containing the price details.
   */
  const renderInformation = (listPrice) => {
    return (
      <>
        <p
          className={`mb-2 px-16 pb-12 pt-0 text-base font-600 md:px-0 md:py-0 ${isMobile ? "border-b border-lightGrey" : ""}`}
        >{`${PRICE} ${DETAILS}`}</p>
        <div className="p-16 md:p-0">
          <div className="flex items-center justify-between pt-3">
            <span className="text-14 font-400 text-black-100">{MAXIMUM_RETAIL_PRICE}</span>
            <span className="pl-24 text-14 font-500 text-black-900">{updateCurrency(selectedCurrency, listPrice)}</span>
          </div>
          <div className="text-sm text-gray-500">{INCLUSIVE_OF_ALL_TAXES}</div>
        </div>
      </>
    );
  };
  return (
    <div className={`relative flex flex-col gap-4 ${containerPadding}`}>
      <Typography
        Tag={"h1"}
        id="productName"
        classes="text-16 md:text-20 xxl:text-24 font-500 text-black-100 leading-6"
        itemProp="name"
      >
        {get(currentItem, "productName")}
      </Typography>

      <div className="relative flex flex-wrap items-baseline gap-8 md:items-center">
        <Typography Tag={"p"} classes="text-16 md:text-24 xxl:text-28 font-600 text-black-100" id="odometer">
          {updateCurrency(selectedCurrency, deliveryDatesAndGeoPrice?.price || get(currentItem, "productPrice.price"))}
        </Typography>

        {(get(currentItem, "productPrice.listPrice") !== get(currentItem, "productPrice.price") ||
          deliveryDatesAndGeoPrice?.price !== deliveryDatesAndGeoPrice?.listPrice) && (
          <div className="flex w-[auto] gap-6">
            <Typography Tag={"p"} classes="text-14 md:text-20 font-400 text-grey-500">
              <del>
                {updateCurrency(
                  selectedCurrency,
                  deliveryDatesAndGeoPrice?.listPrice || get(currentItem, "productPrice.listPrice"),
                )}
              </del>
            </Typography>
            {isEmpty(deliveryDatesAndGeoPrice) ? (
              <Typography Tag={"p"} classes="w-max text-14 md:text-20 font-400 text-green-300 flex">
                {Math.round(
                  ((get(currentItem, "productPrice.listPrice") - get(currentItem, "productPrice.price")) /
                    get(currentItem, "productPrice.listPrice")) *
                    100,
                )}
                % Off
              </Typography>
            ) : (
              <InformationButton
                isMobile={isMobile}
                renderInformation={renderSurgeNotificationMessage()}
                icon="/icons/price-surge-icon.svg"
              />
            )}
          </div>
        )}

        <div className="relative top-3 md:top-0" itemProp="offers" itemScope={true} itemType="https://schema.org/Offer">
          <meta itemProp="url" content={`/gift/${serverData?.urlIdentifier}`} />
          <meta itemProp="priceCurrency" content={isEmpty(selectedCurrency) ? "INR" : selectedCurrency?.currency?.id} />
          <meta itemProp="price" content={get(currentItem, "productPrice.listPrice")} />
          <link itemProp="availability" href={`http://schema.org/${!isOutOfStock ? "InStock" : "OutofStock"}`} />
          <InformationButton
            isMobile={isMobile}
            renderInformation={renderInformation(get(currentItem, "productPrice.listPrice"))}
            icon={constants.infoCircleGraySvg}
          />
        </div>

        {!isEmpty(freeGiftTag) && (
          <Brick
            content={freeGiftTag?.name}
            icon={freeGiftTag?.icon}
            customStyle={{
              backgroundColor: freeGiftTag?.backgroundColor,
              color: freeGiftTag?.fontColor,
              border: "none",
              borderRadius: "8px",
              marginLeft: "12px",
              padding: "4px 8px",
            }}
            contentClass="pl-4 text-10"
          />
        )}
      </div>

      {!isMobile && currentItem?.productReviewsAndRatings?.ratingValue > 0 && (
        <div className="cursor-pointer" onClick={() => onSmoothScrollToElement("reviews-section", 150)}>
          <RateAndReview
            review={{
              ratingValue: currentItem?.productReviewsAndRatings?.ratingValue,
              reviewCount: currentItem?.productReviewsAndRatings?.totalReview,
            }}
            isReviewTextHide={false}
            classes="relative left-0 border-1 top-2 z-0 hover:border-grey ease-in-out duration-300"
            isPriority={false}
          />
        </div>
      )}
    </div>
  );
};

ProductPriceSection.propTypes = {
  containerPadding: PropTypes.string.isRequired,
  currentItem: PropTypes.object.isRequired,
  selectedCurrency: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired,
  serverData: PropTypes.object.isRequired,
  isOutOfStock: PropTypes.bool,
  isInternational: PropTypes.bool,
};

export default ProductPriceSection;
